/**
 * DocumentReviewPage
 *
 * Supervision document review (role-specific):
 * - Supervisor: note quality panel, inline comments, Request Edits, Sign and Lock (supervisor signature).
 * - Provider: read-only note view, Sign note (provider signature) to submit for supervisor review — no caseload navigation required.
 * - Next/Previous note navigation within the supervision queue.
 */

import { createReference } from '@medplum/core';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { practitionerPreferredReference } from '../../lib/loadPractitionerPreferredLabels';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notify } from '@/lib/notify';
import {
  IconArrowLeft,
  IconCalendarEvent,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconHistory,
  IconNote,
  IconSignature,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SignatureCaptureModal } from '../../components/SignatureCapture';
import { SupervisionLayoutProvider } from './context/SupervisionLayoutContext';
import { useCosignQueueFromMedplum } from './hooks/useCosignQueueFromMedplum';
import { useSupervisionTimeline } from './hooks/useSupervisionTimeline';
import {
  DocumentReviewHistoryTab,
  QRDocumentWithComments,
  SupervisionStatusBadge,
} from './components';
import type { SignatureInfo } from '../appointments/fhirNoteMapping';
import {
  APPOINTMENT_TAG_SYSTEM,
  getAiNoteCheckResultsFromQR,
  getReviewCommentsFromQR,
  getSignatureFromQR,
  PROVIDER_SIGNATURE_EXT_URL,
  setReviewCommentsOnQR,
  SUPERVISOR_SIGNATURE_EXT_URL,
} from '../appointments/fhirNoteMapping';
import {
  applyProviderSignatureForDocumentReview,
  applyRequestedEditsForDocumentReview,
  applySupervisorCosignatureForDocumentReview,
  getQueueDocumentReviewId,
  saveProviderSignedDocument,
} from './utils/documentReviewWrites';
import { NoteQualityChecklist } from '@/components/NoteQualityChecklist';
import { useDocumentReviewResponse } from './hooks/useDocumentReviewResponse';
import { useSupervisionScope } from './hooks/useSupervisionScope';
import { requestGroupSharedNoteFanout } from '@/lib/groupSharedNoteFanoutService';
import { isGroupPatientFanoutCopy } from './utils/cosignQueueEligibility';
import { usePermissions } from '../../contexts/PermissionContext';
import { qrToDocumentReviewStatus } from './utils/questionnaireResponseMappings';
import { validateSupervisionMeetingBeforeSign } from './utils/supervisionMeetingFormValidation';
import { SUPERVISION_MEETING_TAG_SYSTEM, SUPERVISION_NOTE_TYPE_TAG_SYSTEM } from './supervisionFhirConstants';
import type { DocumentComment, DocumentCommentAnchor } from './types';
import { fhirUpdateQR } from '../appointments/utils/fhirWrite';
import { isSealPendingError } from '../appointments/utils/attendanceFulfillment';
import { notifySealPending } from '../appointments/utils/sealPendingNotice';
import { scrollToAndFlashQuestionnaireItem } from '../appointments/utils/scrollToQuestionnaireItem';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { getPatientHomeState } from '@unify/fhir-model/resources/patient';

function formatSignatureDateTime(date: Date): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function DocumentReviewContent(): JSX.Element {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  const {
    response: qrResponse,
    questionnaire: qrQuestionnaire,
    loading: qrLoading,
    error: qrError,
    refetch: refetchDocument,
    setResponse: setQrResponse,
  } = useDocumentReviewResponse(documentId);

  const { items: cosignQueueItems, refetch: refetchCosignQueue } = useCosignQueueFromMedplum();
  const { items: ownUnsignedItems, refetch: refetchOwnUnsigned } = useCosignQueueFromMedplum({
    queue: 'own-unsigned',
  });
  const { isAuthorInScope, role, viewerPractitionerId } = useSupervisionScope();

  const isSupervisorView = role === 'supervisor';
  const isProviderView = role === 'provider';
  const refetchQueues = useCallback(() => {
    void refetchCosignQueue();
    void refetchOwnUnsigned();
  }, [refetchCosignQueue, refetchOwnUnsigned]);

  const rbac = usePermissions();
  const canCosign = rbac.permissions?.can_sign_others_charting_notes !== false;
  const { practitioner } = useCurrentUserPractitioner();

  const isSupervisionMeetingNote = useMemo(
    () =>
      Boolean(
        qrResponse?.meta?.tag?.some(
          (tag) => tag.system === SUPERVISION_MEETING_TAG_SYSTEM && tag.code === 'true',
        ),
      ),
    [qrResponse?.meta?.tag],
  );

  // Comments: stored in QR extension (anchor by itemPath/linkId, not line number). Load when document loads.
  const [comments, setComments] = useState<DocumentComment[]>([]);

  // Load comments from QuestionnaireResponse when document loads or after refetch (post-persist).
  // When refetch returns, only sync from server if server has at least our comments (by id) so we don't overwrite optimistic adds.
  const qrResponseRef = useRef(qrResponse);
  qrResponseRef.current = qrResponse;
  useEffect(() => {
    // Keyed on id/versionId: re-read the stored comments only when the server response version changes.
    const response = qrResponseRef.current;
    if (!response) {
      setComments([]);
      return;
    }
    const stored = getReviewCommentsFromQR(response) as DocumentComment[];
    setComments((prev) => {
      const storedIds = new Set(stored.map((c) => c.id));
      const prevIds = new Set(prev.map((c) => c.id));
      const serverHasAllLocal = prevIds.size === 0 || [...prevIds].every((id) => storedIds.has(id));
      return serverHasAllLocal ? (stored.length ? stored : []) : prev;
    });
  }, [qrResponse?.id, qrResponse?.meta?.versionId]);
  const [signModalOpened, { open: openSign, close: closeSign }] = useDisclosure(false);
  const [signIntent, setSignIntent] = useState<'supervisor' | 'provider'>('supervisor');

  const displayResponse = qrResponse;

  const authorPractitionerId = useMemo(() => {
    const ref = qrResponse?.author?.reference;
    if (!ref) return undefined;
    const m = ref.match(/Practitioner\/([^/]+)/);
    return m?.[1];
  }, [qrResponse?.author?.reference]);

  const isOwnDocument = Boolean(
    viewerPractitionerId && authorPractitionerId === viewerPractitionerId,
  );

  const queueItemsWithDocumentId =
    isSupervisorView && isOwnDocument ? ownUnsignedItems : cosignQueueItems;

  /** Provider signature from QR extension (removed when supervisor clicks Request Edits). Use displayResponse so mock "Request Edits" hides it. */
  const providerSignatureFromQR = useMemo(
    () => (displayResponse ? getSignatureFromQR(displayResponse, PROVIDER_SIGNATURE_EXT_URL) : undefined),
    [displayResponse],
  );

  /** Supervisor signature from QR extension. */
  const supervisorSignatureFromQR = useMemo(
    () => (displayResponse ? getSignatureFromQR(displayResponse, SUPERVISOR_SIGNATURE_EXT_URL) : undefined),
    [displayResponse],
  );

  /** Scroll to top when navigating to a different note (Previous/Next). */
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [documentId]);

  const doc = queueItemsWithDocumentId.find(
    (d) => (d as { documentIdForReview: string }).documentIdForReview === documentId,
  );
  const [clientJurisdictionState, setClientJurisdictionState] = useState<string>();
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let patientRef = qrResponse?.subject?.reference;
      if (!patientRef?.startsWith('Patient/')) {
        const appointmentId = doc?.appointmentId
          ?? qrResponse?.meta?.tag?.find((tag) => tag.system === APPOINTMENT_TAG_SYSTEM)?.code;
        if (appointmentId) {
          const appointment = await medplum.readResource('Appointment', appointmentId);
          patientRef = appointment.participant
            ?.map((participant) => participant.actor?.reference)
            .find((reference) => reference?.startsWith('Patient/'));
        }
      }
      if (!patientRef?.startsWith('Patient/')) return undefined;
      const patient = await medplum.readResource('Patient', patientRef.slice('Patient/'.length));
      return getPatientHomeState(patient);
    })().then((state) => {
      if (!cancelled) setClientJurisdictionState(state);
    }).catch(() => {
      if (!cancelled) setClientJurisdictionState(undefined);
    });
    return () => { cancelled = true; };
  }, [doc?.appointmentId, medplum, qrResponse?.meta?.tag, qrResponse?.subject?.reference]);
  const { auditEntries, noteEdits } = useSupervisionTimeline(documentId);

  const aiCheckPayload = displayResponse ? getAiNoteCheckResultsFromQR(displayResponse) : undefined;
  const automaticChecks =
    aiCheckPayload?.status === 'completed' ? aiCheckPayload.checks : undefined;
  const automaticReviewState =
    aiCheckPayload?.status === 'completed'
      ? ('completed' as const)
      : aiCheckPayload?.status === 'failed'
        ? ('failed' as const)
        : ('not_started' as const);

  const accessDenied = Boolean(
    qrResponse &&
      authorPractitionerId &&
      !isAuthorInScope(authorPractitionerId) &&
      authorPractitionerId !== viewerPractitionerId,
  );

  const status =
    (displayResponse ? qrToDocumentReviewStatus(displayResponse) : undefined) ??
    doc?.status ??
    'draft';
  const isCosigned = status === 'cosigned';
  const isIndividualGroupNote = Boolean(qrResponse && isGroupPatientFanoutCopy(qrResponse));
  const showSuperviseeReviewActions = isSupervisorView && !isOwnDocument;
  const showOwnSignActions = isProviderView || (isSupervisorView && isOwnDocument);
  /** Supervisor co-sign: provider already signed, awaiting supervisor. */
  const canSupervisorSign = showSuperviseeReviewActions && status === 'signed' && !isIndividualGroupNote;
  /** Own unsigned notes: sign drafts. Providers may also re-sign after edits-requested. */
  const canProviderSign =
    showOwnSignActions &&
    !isIndividualGroupNote &&
    !providerSignatureFromQR &&
    (status === 'draft' || (isProviderView && status === 'edits-requested')) &&
    !qrLoading;

  const syncSharedGroupCopies = useCallback(
    async (qr: QuestionnaireResponse, strict: boolean): Promise<void> => {
      if (!qr.id || !qr.subject?.reference?.startsWith('Group/')) return;
      const appointmentId =
        doc?.appointmentId
        ?? qr.meta?.tag?.find((t) => t.system === APPOINTMENT_TAG_SYSTEM)?.code;
      if (!appointmentId) return;
      await requestGroupSharedNoteFanout(medplum, qr.id, appointmentId, { strict });
    },
    [doc?.appointmentId, medplum],
  );

  const queueIndex = useMemo(
    () =>
      queueItemsWithDocumentId.findIndex(
        (d) => (d as { documentIdForReview: string }).documentIdForReview === documentId,
      ),
    [documentId, queueItemsWithDocumentId],
  );
  const prevDoc = queueIndex > 0 ? queueItemsWithDocumentId[queueIndex - 1] : null;
  const nextDoc =
    queueIndex >= 0 && queueIndex < queueItemsWithDocumentId.length - 1
      ? queueItemsWithDocumentId[queueIndex + 1]
      : null;

  const handleBack = useCallback(() => {
    navigate('/dashboard/supervision/cosign-notes');
  }, [navigate]);

  const persistComments = useCallback(
    async (nextComments: DocumentComment[]) => {
      if (!qrResponse?.id) return;
      const updated = setReviewCommentsOnQR(qrResponse, nextComments);
      try {
        await medplum.updateResource(updated);
        await refetchDocument();
      } catch {
        notify({
          title: 'Could not save comments',
          message: 'Please try again.',
          type: 'error',
        });
      }
    },
    [medplum, qrResponse, refetchDocument],
  );

  const handleAddComment = useCallback(
    (anchor: DocumentCommentAnchor, text: string) => {
      const newComment: DocumentComment = {
        id: `c-${Date.now()}`,
        authorId: 'super-1',
        authorName: 'You',
        authorRole: 'supervisor',
        text,
        createdAt: new Date().toISOString(),
        anchor,
      };
      setComments((prev) => {
        const next = [...prev, newComment];
        persistComments(next);
        return next;
      });
      notify({
        icon: <IconCheck size={18} />,
        title: 'Comment added',
        message: '',
        type: 'success',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
    },
    [persistComments],
  );

  const handleResolveComment = useCallback(
    (commentId: string) => {
      setComments((prev) => {
        const next = prev.map((c) =>
          c.id === commentId ? { ...c, resolvedAt: new Date().toISOString() } : c
        );
        persistComments(next);
        return next;
      });
      notify({
        icon: <IconCheck size={18} />,
        title: 'Comment resolved',
        message: '',
        type: 'success',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
    },
    [persistComments],
  );

  const handleRequestEdits = useCallback(async () => {
    if (!qrResponse?.id) return;
    try {
      const updated = applyRequestedEditsForDocumentReview(qrResponse);
      await medplum.updateResource(updated);
      await syncSharedGroupCopies(updated, false);
      await refetchDocument();
      const supervisee = doc?.clinicianName ?? qrResponse?.author?.display ?? 'the supervisee';
      notify({
        title: 'Edits requested',
        message: `Note returned to ${supervisee}. Provider signature removed; they can edit and sign again to resubmit.`,
        type: 'info',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
    } catch {
      notify({
        title: 'Could not return note',
        message: 'Please try again.',
        type: 'error',
      });
    }
  }, [doc?.clinicianName, medplum, qrResponse, refetchDocument, syncSharedGroupCopies]);

  const handleSignAndLock = useCallback(
    async (signature: SignatureInfo) => {
      if (!qrResponse?.id) return;
      const advanceToDocumentId = nextDoc ? getQueueDocumentReviewId(nextDoc) : undefined;
      try {
        const updated = applySupervisorCosignatureForDocumentReview(qrResponse, signature);
        const result = await fhirUpdateQR(medplum, { ...updated, id: qrResponse.id });
        await syncSharedGroupCopies(result, true);
        medplum.invalidateSearches('QuestionnaireResponse');
        setQrResponse(result);
        void refetchQueues();
        closeSign();
        notify({
          icon: <IconCheck size={18} />,
          title: 'Document co-signed and locked',
          message: 'Co-signed successfully. This note is now locked.',
          type: 'success',
          withCloseButton: true,
          withBorder: true,
          styles: { root: { borderColor: '#9CA3AF' } },
        });
        if (advanceToDocumentId) {
          navigate(`/dashboard/supervision/review/${advanceToDocumentId}`);
        } else if (!isSupervisionMeetingNote) {
          navigate('/dashboard/supervision/cosign-notes');
        }
      } catch (e) {
        notify({
          title: 'Could not save signature',
          error: e,
          message: 'Please try again.',
          type: 'error',
        });
      }
    },
    [closeSign, isSupervisionMeetingNote, medplum, navigate, nextDoc, qrResponse, refetchQueues, setQrResponse, syncSharedGroupCopies],
  );

  const handleProviderSign = useCallback(
    async (signature: SignatureInfo) => {
      if (!qrResponse?.id) return;
      const advanceToDocumentId = nextDoc ? getQueueDocumentReviewId(nextDoc) : undefined;
      if (qrQuestionnaire && isSupervisionMeetingNote) {
        const validation = validateSupervisionMeetingBeforeSign({
          questionnaire: qrQuestionnaire,
          response: qrResponse,
          practitioner: practitioner ?? (profile?.resourceType === 'Practitioner' ? profile : null),
        });
        if (!validation.success) {
          if (validation.missingItemPath) {
            scrollToAndFlashQuestionnaireItem(validation.missingItemPath);
          }
          notify({
            type: 'error',
            title: 'Missing required fields',
            // eslint-disable-next-line no-restricted-syntax -- authored validation text from our own code, not a caught error
            message: `${validation.message} Complete the required fields before signing.`,
          });
          closeSign();
          return;
        }
      }
      try {
        let updated = applyProviderSignatureForDocumentReview(qrResponse, signature);
        if (!updated.author?.reference && profile) {
          updated = {
            ...updated,
            author: profile.resourceType === 'Practitioner'
              ? practitionerPreferredReference(profile)
              : createReference(profile),
          };
        }
        let result: QuestionnaireResponse;
        let sealPending = false;
        try {
          result = await saveProviderSignedDocument(medplum, { ...updated, id: qrResponse.id });
        } catch (saveErr) {
          // Signed, but the Appointment seal is pending: keep the signed note and offer a seal-only retry.
          if (!isSealPendingError(saveErr)) throw saveErr;
          result = saveErr.signedNote;
          sealPending = true;
          notifySealPending(medplum, saveErr);
        }
        await syncSharedGroupCopies(result, true);
        medplum.invalidateSearches('QuestionnaireResponse');
        setQrResponse(result);
        void refetchQueues();
        closeSign();
        if (!sealPending) notify({
          icon: <IconCheck size={18} />,
          title: 'Note signed',
          message: isOwnDocument
            ? 'Your note was signed.'
            : 'Your note was submitted for supervisor review.',
          type: 'success',
          withCloseButton: true,
          withBorder: true,
          styles: { root: { borderColor: '#9CA3AF' } },
        });
        if (advanceToDocumentId) {
          navigate(`/dashboard/supervision/review/${advanceToDocumentId}`);
        }
      } catch (e) {
        notify({
          title: 'Could not save signature',
          error: e,
          message: 'Please try again.',
          type: 'error',
        });
      }
    },
    [closeSign, isOwnDocument, isSupervisionMeetingNote, medplum, navigate, nextDoc, practitioner, profile, qrQuestionnaire, qrResponse, refetchQueues, setQrResponse, syncSharedGroupCopies],
  );

  const openSupervisorSignModal = useCallback(() => {
    setSignIntent('supervisor');
    openSign();
  }, [openSign]);

  const openProviderSignModal = useCallback(() => {
    if (qrQuestionnaire && qrResponse && isSupervisionMeetingNote) {
      const validation = validateSupervisionMeetingBeforeSign({
        questionnaire: qrQuestionnaire,
        response: qrResponse,
        practitioner: practitioner ?? (profile?.resourceType === 'Practitioner' ? profile : null),
      });
      if (!validation.success) {
        notify({
          type: 'error',
          title: 'Missing required fields',
          // eslint-disable-next-line no-restricted-syntax -- authored validation text from our own code, not a caught error
          message: `${validation.message} Complete the required fields before signing.`,
        });
        return;
      }
    }
    setSignIntent('provider');
    openSign();
  }, [isSupervisionMeetingNote, openSign, practitioner, profile, qrQuestionnaire, qrResponse]);

  const handlePrev = useCallback(() => {
    if (prevDoc) {
      navigate(`/dashboard/supervision/review/${getQueueDocumentReviewId(prevDoc)}`);
    }
  }, [navigate, prevDoc]);

  const handleNext = useCallback(() => {
    if (nextDoc) {
      navigate(`/dashboard/supervision/review/${getQueueDocumentReviewId(nextDoc)}`);
    }
  }, [navigate, nextDoc]);

  const docTitle = useMemo(() => {
    if (isSupervisionMeetingNote && qrResponse) {
      const tag = qrResponse.meta?.tag?.find((t) => t.system === SUPERVISION_NOTE_TYPE_TAG_SYSTEM);
      if (tag?.code === 'clinical-supervision') return 'Clinical Supervision Note';
      if (tag?.code === 'clinical-supervisor') return 'Clinical Supervisor Note';
      return 'Supervision Note';
    }
    return doc?.title ?? qrQuestionnaire?.title ?? 'Document';
  }, [doc?.title, isSupervisionMeetingNote, qrQuestionnaire?.title, qrResponse]);

  const docSubtitle = useMemo(() => {
    if (isSupervisionMeetingNote && qrResponse) {
      const parts: string[] = [];
      if (qrResponse.author?.display) parts.push(qrResponse.author.display);
      if (qrResponse.authored) {
        parts.push(formatSignatureDateTime(new Date(qrResponse.authored)));
      }
      return parts.length > 0 ? parts.join(' · ') : undefined;
    }
    return doc
      ? [doc.clinicianName, doc.clientName].filter(Boolean).join(' · ')
      : (qrResponse?.subject?.display ?? undefined);
  }, [doc, isSupervisionMeetingNote, qrResponse]);

  const linkedAppointmentId = doc?.appointmentId
    ?? qrResponse?.meta?.tag?.find((t) => t.system === APPOINTMENT_TAG_SYSTEM)?.code;

  if (!qrLoading && !doc && !qrResponse) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
            Back to Document Review
          </Button>
          <Alert variant="light" color="orange" title="Document not found">
            The requested document could not be found or you may not have access.
          </Alert>
        </Stack>
      </Container>
    );
  }

  if (!qrLoading && qrResponse && accessDenied) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
            {isProviderView ? 'Back to unsigned notes' : 'Back to co-sign queue'}
          </Button>
          <Alert variant="light" color="orange" title="Not available">
            This document is not in your supervision scope (it is not assigned to you as a provider or linked to your
            supervisees).
          </Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Stack gap="xl" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <Button variant="subtle" size="sm" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
              Back
            </Button>
            <div>
              <Title order={3}>{docTitle}</Title>
              {docSubtitle && (
                <Text size="sm" c="gray.6">
                  {docSubtitle}
                </Text>
              )}
            </div>
          </Group>
          <Group gap="sm">
            {linkedAppointmentId && (
              <Button
                variant="light"
                size="sm"
                leftSection={<IconCalendarEvent size={16} />}
                onClick={() => navigate(`/dashboard/appointments/${linkedAppointmentId}`)}
              >
                Open in Appointment
              </Button>
            )}
            <SupervisionStatusBadge status={status} size="md" />
          </Group>
        </Group>

        {isProviderView && status === 'signed' && (
          <Alert color="blue" variant="light" title="Submitted for supervisor review">
            You have signed this note. Your supervisor will co-sign when ready.
          </Alert>
        )}

        <Tabs defaultValue="note" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Tabs.List>
            <Tabs.Tab value="note" leftSection={<IconNote size={16} />}>
              Note
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
              History
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="note" pt="md" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Grid gutter="md" style={{ flex: 1, minHeight: 0 }}>
              <Grid.Col
                span={{ base: 12, lg: showSuperviseeReviewActions && !isSupervisionMeetingNote ? 8 : 12 }}
                style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}
              >
                <Card withBorder style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <QRDocumentWithComments
                    questionnaireResponse={displayResponse}
                    questionnaire={qrQuestionnaire}
                    comments={comments}
                    onAddComment={handleAddComment}
                    onResolveComment={handleResolveComment}
                    isSigned={isCosigned}
                    readOnly={isProviderView}
                    loading={qrLoading}
                    error={qrError}
                    hideTitle={isSupervisionMeetingNote}
                  />
                  <Box
                    mt="lg"
                    pt="lg"
                    style={{
                      borderTop: '1px solid var(--mantine-color-gray-3)',
                    }}
                  >
                    <Text size="sm" fw={600} mb="md">
                      Signatures
                    </Text>
                    <Grid gutter="md">
                      {providerSignatureFromQR && (
                        <Grid.Col span={supervisorSignatureFromQR ? { base: 12, sm: 6 } : 12}>
                          <Box
                            p="md"
                            style={{
                              backgroundColor: 'var(--mantine-color-blue-0)',
                              borderRadius: 'var(--mantine-radius-md)',
                              minHeight: 160,
                            }}
                          >
                            <Text size="sm" c="gray.6" mb={6}>
                              Provider
                            </Text>
                            {providerSignatureFromQR.signatureData ? (
                              <img
                                src={`data:${providerSignatureFromQR.signatureFormat ?? 'image/png'};base64,${providerSignatureFromQR.signatureData}`}
                                alt={`Signature of ${providerSignatureFromQR.name}`}
                                style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                              />
                            ) : (
                              <Text
                                size="xl"
                                fw={600}
                                c="gray.8"
                                style={{ fontFamily: "'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive" }}
                              >
                                {providerSignatureFromQR.name}
                              </Text>
                            )}
                            {providerSignatureFromQR.license && (
                              <Text size="md" c="gray.7" mt={6} style={{ whiteSpace: 'pre-line' }}>
                                {providerSignatureFromQR.license}
                              </Text>
                            )}
                            {providerSignatureFromQR.signedAt && (
                              <Text size="sm" c="gray.6" mt={6}>
                                {formatSignatureDateTime(new Date(providerSignatureFromQR.signedAt))}
                              </Text>
                            )}
                            <Text size="sm" c="gray.6" mt={6}>
                              IP: {providerSignatureFromQR.ipAddress ?? '—'}
                            </Text>
                          </Box>
                        </Grid.Col>
                      )}
                      {supervisorSignatureFromQR && (
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <Box
                            p="md"
                            style={{
                              backgroundColor: 'var(--mantine-color-blue-0)',
                              borderRadius: 'var(--mantine-radius-md)',
                              minHeight: 160,
                            }}
                          >
                            <Text size="sm" c="gray.6" mb={6}>
                              Supervisor
                            </Text>
                            {supervisorSignatureFromQR.signatureData ? (
                              <img
                                src={`data:${supervisorSignatureFromQR.signatureFormat ?? 'image/png'};base64,${supervisorSignatureFromQR.signatureData}`}
                                alt={`Signature of ${supervisorSignatureFromQR.name}`}
                                style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                              />
                            ) : (
                              <Text
                                size="xl"
                                fw={600}
                                c="gray.8"
                                style={{
                                  fontFamily: "'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive",
                                }}
                              >
                                {supervisorSignatureFromQR.name}
                              </Text>
                            )}
                            {supervisorSignatureFromQR.license && (
                              <Text size="md" c="gray.7" mt={6} style={{ whiteSpace: 'pre-line' }}>
                                {supervisorSignatureFromQR.license}
                              </Text>
                            )}
                            {supervisorSignatureFromQR.signedAt && (
                              <Text size="sm" c="gray.6" mt={6}>
                                {formatSignatureDateTime(new Date(supervisorSignatureFromQR.signedAt))}
                              </Text>
                            )}
                            <Text size="sm" c="gray.6" mt={6}>
                              IP: {supervisorSignatureFromQR.ipAddress ?? '—'}
                            </Text>
                          </Box>
                        </Grid.Col>
                      )}
                    </Grid>
                  </Box>
                </Card>
              </Grid.Col>
              {isSupervisorView && !isOwnDocument && !isSupervisionMeetingNote ? (
                <Grid.Col span={{ base: 12, lg: 4 }}>
                  <Card withBorder p="md">
                    <NoteQualityChecklist
                      automaticChecks={automaticChecks}
                      automaticReviewState={automaticReviewState}
                      automaticReviewCheckedAt={aiCheckPayload?.checkedAt}
                      automaticReviewError={aiCheckPayload?.error}
                      showManualReview
                      questionnaireResponse={displayResponse}
                    />
                  </Card>
                </Grid.Col>
              ) : null}
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Card withBorder p="lg">
              <DocumentReviewHistoryTab
                auditEntries={auditEntries}
                comments={comments}
                noteEdits={noteEdits}
              />
            </Card>
          </Tabs.Panel>
        </Tabs>

        <Card withBorder>
          <Group justify="space-between" wrap="wrap">
            <Group gap="sm">
              {showSuperviseeReviewActions ? (
                <>
                  <Tooltip
                    label={
                      !canCosign
                        ? 'You do not have permission to co-sign notes'
                        : canSupervisorSign
                          ? 'Co-sign and lock this progress note'
                          : 'Provider must sign first; document may already be locked'
                    }
                  >
                    <span>
                      <Button
                        color="green"
                        leftSection={<IconCheck size={16} />}
                        onClick={openSupervisorSignModal}
                        disabled={!canCosign || !canSupervisorSign}
                        variant={canCosign && canSupervisorSign ? 'filled' : 'light'}
                      >
                        Co-sign and Lock
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip label={isCosigned ? 'Document is co-signed and locked' : 'Return note to clinician for edits'}>
                    <span>
                      <Button
                        leftSection={<IconEdit size={16} />}
                        onClick={handleRequestEdits}
                        disabled={isCosigned}
                      >
                        Request Edits
                      </Button>
                    </span>
                  </Tooltip>
                  {isCosigned && (
                    <Badge size="lg" color="green" variant="light">
                      Co-signed
                    </Badge>
                  )}
                </>
              ) : null}
              {showOwnSignActions ? (
                <>
                  <Tooltip
                    label={
                      canProviderSign
                        ? isSupervisorView
                          ? 'Sign this note'
                          : 'Apply your provider signature and submit for supervisor review'
                        : status === 'signed'
                          ? isSupervisorView
                            ? 'Already signed'
                            : 'Already signed — waiting for supervisor co-signature'
                          : isCosigned
                            ? 'This note is locked'
                            : 'Cannot sign yet'
                    }
                  >
                    <span>
                      <Button
                        color="green"
                        leftSection={<IconSignature size={16} />}
                        onClick={openProviderSignModal}
                        disabled={!canProviderSign}
                        variant={canProviderSign ? 'filled' : 'light'}
                      >
                        Sign note
                      </Button>
                    </span>
                  </Tooltip>
                  {status === 'signed' && (
                    <Badge size="lg" color={isSupervisorView ? 'green' : 'blue'} variant="light">
                      {isSupervisorView ? 'Signed' : 'Awaiting supervisor'}
                    </Badge>
                  )}
                  {isCosigned && (
                    <Badge size="lg" color="green" variant="light">
                      Locked
                    </Badge>
                  )}
                </>
              ) : null}
            </Group>
            <Group gap="xs">
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconChevronLeft size={16} />}
                onClick={handlePrev}
                disabled={!prevDoc}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                rightSection={<IconChevronRight size={16} />}
                onClick={handleNext}
                disabled={!nextDoc}
              >
                Next
              </Button>
            </Group>
          </Group>
        </Card>

        <SignatureCaptureModal
          opened={signModalOpened}
          onClose={closeSign}
          onSign={signIntent === 'provider' ? handleProviderSign : handleSignAndLock}
          title={signIntent === 'provider' ? 'Sign progress note' : 'Co-sign and lock progress note'}
          submitLabel={signIntent === 'provider' ? 'Sign note' : 'Co-sign and lock'}
          jurisdictionState={clientJurisdictionState}
        />
      </Stack>
    </Container>
  );
}

export function DocumentReviewPage(): JSX.Element {
  return (
    <SupervisionLayoutProvider>
      <DocumentReviewContent />
    </SupervisionLayoutProvider>
  );
}
